import { FC, useState } from 'react';

import classnames from 'classnames';
import { BannerImageCropType } from 'utils/constants/banner';

import { MediaViewer, NavBar, Text, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { SyncInitialGalleryItem } from 'components/Admin/ModerationList/CampaignModeration/Banner/BannerImages/SyncInitialGalleryItem';

import { BannerModerationInfo } from 'store/models/moderation';
import { useSelector } from 'store/utils';

import styles from 'components/Admin/ModerationList/CampaignModeration/Banner/BannerImages/BannerImages.module.less';

const TrlKeys = {
    title: 'admin.moderation.banner.images.title',
    expand: 'admin.moderation.banner.images.expand',
};

const LOGO_CLASS = {
    [BannerImageCropType.HH]: styles.bannerImageSourceLogoHh,
    [BannerImageCropType.VK]: styles.bannerImageSourceLogoVk,
    [BannerImageCropType.YandexAndVK]: styles.bannerImageSourceLogoYandexAndVk,
};

export const BannerImages: FC<{ imagesUrls: BannerModerationInfo['imageUrls']; bannerId: number }> = ({
    imagesUrls,
    bannerId,
}) => {
    const trls = useTrls();
    const webdavPublicUrl = useSelector((state) => state.clickme.webdavPublicUrl);

    const imagesArray = Object.entries(imagesUrls);

    const [isImagesGalleryVisible, setIsImagesGalleryVisibleOn, setIsImagesGalleryVisibleOff] = useOnOffState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

    return (
        <VSpacingContainer default={8}>
            <Text typography="title-5-semibold">{trls(TrlKeys.title)}</Text>
            <div className={styles.images}>
                {imagesArray.map(([cropType, url], index) => {
                    return (
                        <div key={url}>
                            <div
                                className={styles.bannerMediaPreviewWrapper}
                                onClick={() => {
                                    setIsImagesGalleryVisibleOn();
                                    setSelectedImageIndex(index);
                                }}
                            >
                                <img className={styles.media} src={`${webdavPublicUrl}/${url}`} />
                                <div
                                    className={styles.bannerMediaExpand}
                                    data-qa={`banner-image-expand-${bannerId}-${cropType}`}
                                >
                                    <Text typography="label-3-regular" style="constant">
                                        {trls(TrlKeys.expand)}
                                    </Text>
                                </div>
                                <div
                                    className={classnames(
                                        styles.bannerImageSourceLogo,
                                        LOGO_CLASS[cropType as BannerImageCropType]
                                    )}
                                />
                            </div>
                            <VSpacing default={8} />
                            <div className={styles.mediaDimenshions}>
                                <Text Element="span" typography="label-3-regular" style="secondary">
                                    {trls(`AdCreate.AdForm.CropImage.sizes.${cropType}`)}
                                </Text>
                            </div>
                        </div>
                    );
                })}
            </div>

            <MediaViewer visible={isImagesGalleryVisible} onClose={setIsImagesGalleryVisibleOff}>
                <MediaViewer.Header>
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Actions
                                right={
                                    <MediaViewer.CloseTool>
                                        <CrossOutlinedSize24
                                            initialColor="constant"
                                            padding={8}
                                            borderRadius="halfHeight"
                                            tabIndex={0}
                                        />
                                    </MediaViewer.CloseTool>
                                }
                            />
                        </NavBar.Pane>
                    </NavBar>
                </MediaViewer.Header>
                <MediaViewer.Gallery>
                    {imagesArray.map(([cropType, url]) => {
                        return (
                            <MediaViewer.GalleryItem
                                key={url}
                                src={`${webdavPublicUrl}/${url}`}
                                description={trls(`AdCreate.AdForm.CropImage.sources.${cropType}`)}
                            />
                        );
                    })}
                </MediaViewer.Gallery>
                <SyncInitialGalleryItem index={selectedImageIndex} />
                <MediaViewer.Footer>
                    <MediaViewer.Preview />
                    <MediaViewer.Toolbar />
                </MediaViewer.Footer>
            </MediaViewer>
        </VSpacingContainer>
    );
};
